import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getLeads,
  getLeadsStatus,
  setLeadsList,
} from '../../../store/slices/leadsSlice';
import { extractList } from '../../../utils/response';
import { getErrorMessage } from '../../../utils/errorHandler';
import { getUsersList } from '../../../store/slices/userSlice';
import { buildQueryParams } from '../utils';
import { useAuth } from '../../../hooks/useAuth';

const mapUserFilterOption = (u) => ({
  id: u?.uuid ?? u?.id,
  name: u?.name || u?.username || u?.email || 'Unnamed',
});

function looksLikeStatusId(value) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  return (
    /^\d+$/.test(raw) ||
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)
  );
}

function normalizeStatusLabel(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, ' ')
    .trim();
}

function filtersFromSearchParams(searchParams) {
  const statusIdParam = String(
    searchParams.get('status_id') || searchParams.get('status') || ''
  ).trim();
  const statusName = String(searchParams.get('status_name') || '').trim();
  const statusId = looksLikeStatusId(statusIdParam) ? statusIdParam : '';

  return {
    status_id: statusId,
    status_name: statusName || (!statusId ? statusIdParam : ''),
    assignee_id: '',
    author_id: '',
    date_from: String(searchParams.get('date_from') || '').trim(),
    date_to: String(searchParams.get('date_to') || '').trim(),
  };
}

export const useLeads = (canViewListLead = true) => {
  const dispatch = useDispatch();
  const { hasPermission } = useAuth();
  const leadsState = useSelector((state) => state.leads);
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 10,
    total: 0,
  });
  const [noActivity, setNoActivity] = useState({
    no_activity_since_days: '',
  });
  const [filters, setFilters] = useState(() => filtersFromSearchParams(searchParams));
  const [statusDeepLinkReady, setStatusDeepLinkReady] = useState(() => {
    const initial = filtersFromSearchParams(searchParams);
    // Wait to fetch until status_name can be resolved to status_id
    return !(initial.status_name && !initial.status_id);
  });
  const [sort, setSort] = useState({
    sort_by: 'updated_at',
    sort_direction: 'desc',
  });
  const [processingId, setProcessingId] = useState(null);

  /** Filter dropdown options — loaded only when the matching dropdown opens */
  const [statusFilterOptions, setStatusFilterOptions] = useState([]);
  const [usersFilterOptions, setUsersFilterOptions] = useState([]);
  const [statusFilterLoading, setStatusFilterLoading] = useState(false);
  const [usersFilterLoading, setUsersFilterLoading] = useState(false);
  const statusFilterFetchedRef = useRef(false);
  const usersFilterFetchedRef = useRef(false);
  const statusFilterInFlightRef = useRef(null);
  const usersFilterInFlightRef = useRef(null);
  const statusUrlResolvedRef = useRef(false);

  const leadsResponse = leadsState.leads;
  const leads = extractList(leadsResponse);
  const tableLoading = leadsState.loading;

  const fetchLeads = useCallback(
    async (page = pagination.page, pageSize = pagination.pageSize, search = searchValue) => {
      if (!canViewListLead) {
        toast.error('You do not have permission to view leads.');
        return;
      }
      const params = buildQueryParams(
        { page, pageSize },
        filters,
        sort,
        noActivity,
        search
      );
      try {
        const res = await dispatch(getLeads(params)).unwrap();
        if (res?.success && res?.data?.pagination) {
          setPagination((p) => ({ ...p, total: res.data.pagination?.total || 0 }));
        }
      } catch (e) {
        toast.error(getErrorMessage(e) || 'Failed to fetch leads');
      }
    },
    [
      canViewListLead,
      dispatch,
      filters,
      sort,
      noActivity,
      pagination.page,
      pagination.pageSize,
      searchValue,
    ]
  );

  const ensureStatusFilterOptions = useCallback(async () => {
    if (statusFilterOptions.length > 0) return statusFilterOptions;
    if (statusFilterInFlightRef.current) return statusFilterInFlightRef.current;

    setStatusFilterLoading(true);
    const request = (async () => {
      try {
        const data = await dispatch(
          getLeadsStatus({ 'conditions[model]': 'Lead' })
        ).unwrap();
        const list = extractList(data);
        setStatusFilterOptions(list);
        statusFilterFetchedRef.current = true;
        return list;
      } catch (e) {
        statusFilterFetchedRef.current = false;
        toast.error(getErrorMessage(e) || 'Failed to load statuses');
        return [];
      } finally {
        setStatusFilterLoading(false);
        statusFilterInFlightRef.current = null;
      }
    })();

    statusFilterInFlightRef.current = request;
    return request;
  }, [dispatch, statusFilterOptions]);

  const ensureUsersFilterOptions = useCallback(async () => {
    if (!hasPermission('user-list')) {
      toast.error('You do not have permission to view users.');
      return [];
    }
    if (usersFilterOptions.length > 0) return usersFilterOptions;
    if (usersFilterInFlightRef.current) return usersFilterInFlightRef.current;

    setUsersFilterLoading(true);
    const request = (async () => {
      try {
        const data = await dispatch(getUsersList({ paginate: 0 })).unwrap();
        const list = extractList(data).map(mapUserFilterOption).filter((u) => u.id != null);
        setUsersFilterOptions(list);
        usersFilterFetchedRef.current = true;
        return list;
      } catch (e) {
        usersFilterFetchedRef.current = false;
        toast.error(getErrorMessage(e) || 'Failed to load users');
        return [];
      } finally {
        setUsersFilterLoading(false);
        usersFilterInFlightRef.current = null;
      }
    })();

    usersFilterInFlightRef.current = request;
    return request;
  }, [dispatch, hasPermission, usersFilterOptions]);

  // Resolve status_id from status_name (dashboard deep-link) and keep dropdown in sync
  useEffect(() => {
    if (!canViewListLead || statusUrlResolvedRef.current) return;

    const needsNameResolve = Boolean(filters.status_name) && !filters.status_id;
    const needsNameFromId = Boolean(filters.status_id) && !filters.status_name;
    if (!needsNameResolve && !needsNameFromId) {
      statusUrlResolvedRef.current = true;
      setStatusDeepLinkReady(true);
      return;
    }

    let cancelled = false;
    (async () => {
      const list = await ensureStatusFilterOptions();
      if (cancelled) return;

      if (needsNameResolve) {
        const match = list.find(
          (s) => normalizeStatusLabel(s?.name) === normalizeStatusLabel(filters.status_name)
        );
        if (match?.id != null) {
          setFilters((prev) => ({
            ...prev,
            status_id: match.id,
            status_name: match.name || prev.status_name,
          }));
          const next = new URLSearchParams(searchParams);
          next.delete('status');
          next.delete('status_name');
          next.set('status_id', String(match.id));
          setSearchParams(next, { replace: true });
        }
      } else if (needsNameFromId) {
        const match = list.find((s) => String(s?.id) === String(filters.status_id));
        if (match?.name) {
          setFilters((prev) => ({
            ...prev,
            status_name: match.name,
          }));
        }
        const next = new URLSearchParams(searchParams);
        next.delete('status');
        next.delete('status_name');
        next.set('status_id', String(filters.status_id));
        setSearchParams(next, { replace: true });
      }

      statusUrlResolvedRef.current = true;
      setStatusDeepLinkReady(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [canViewListLead, filters.status_id, filters.status_name, ensureStatusFilterOptions, searchParams, setSearchParams]);

  useEffect(() => {
    if (!canViewListLead || !statusDeepLinkReady) return;

    const delay = searchValue ? 500 : 0;
    const timer = setTimeout(() => {
      fetchLeads(pagination.page, pagination.pageSize, searchValue);
    }, delay);

    return () => clearTimeout(timer);
  }, [
    canViewListLead,
    statusDeepLinkReady,
    pagination.page,
    pagination.pageSize,
    filters,
    sort,
    noActivity,
    searchValue,
    fetchLeads,
  ]);

  const syncStatusSearchParams = (nextFilters) => {
    const next = new URLSearchParams(searchParams);
    next.delete('status');
    next.delete('status_name');
    if (nextFilters.status_id) {
      next.set('status_id', String(nextFilters.status_id));
    } else {
      next.delete('status_id');
    }
    setSearchParams(next, { replace: true });
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'status_id') {
        const opt = statusFilterOptions.find((s) => String(s?.id) === String(value));
        next.status_name = opt?.name || '';
        syncStatusSearchParams(next);
      }
      return next;
    });
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  };

  /** Same as dashboard deep-link: set status_id (+ optional name) and sync URL */
  const applyStatusFilter = (statusId = '', statusName = '') => {
    setFilters((prev) => {
      const next = {
        ...prev,
        status_id: statusId ? String(statusId) : '',
        status_name: statusName || '',
      };
      if (next.status_id && !next.status_name) {
        const opt = statusFilterOptions.find((s) => String(s?.id) === String(next.status_id));
        next.status_name = opt?.name || '';
      }
      if (!next.status_id) next.status_name = '';
      syncStatusSearchParams(next);
      return next;
    });
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  };

  const handleDateRangeChange = ({ date_from = '', date_to = '' } = {}) => {
    setFilters((prev) => ({
      ...prev,
      date_from: date_from || '',
      date_to: date_to || '',
    }));
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  };

  const handleSortChange = (fieldOrSort) => {
    if (fieldOrSort && typeof fieldOrSort === 'object' && !Array.isArray(fieldOrSort)) {
      setSort({
        sort_by: fieldOrSort.sort_by ?? 'updated_at',
        sort_direction: fieldOrSort.sort_direction ?? 'desc',
      });
      return;
    }
    setSort((prev) => ({
      sort_by: fieldOrSort,
      sort_direction:
        prev.sort_by === fieldOrSort && prev.sort_direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const clearFilters = () => {
    const cleared = {
      status_id: '',
      status_name: '',
      assignee_id: '',
      author_id: '',
      date_from: '',
      date_to: '',
    };
    setFilters(cleared);
    setSort({ sort_by: 'updated_at', sort_direction: 'desc' });
    setNoActivity({ no_activity_since_days: '' });
    setPagination((p) => ({ ...p, page: 0 }));
    syncStatusSearchParams(cleared);
  };

  const handleNoActivityChange = (value) => {
    setNoActivity({ no_activity_since_days: value });
  };

  const getActiveFiltersCount = () => {
    const filterCount = Object.entries(filters).filter(([key, v]) => {
      if (key === 'status_name') return false;
      return v !== '' && v !== null && v !== undefined;
    }).length;
    return filterCount + (noActivity.no_activity_since_days ? 1 : 0);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setPagination((p) => (p.page === 0 ? p : { ...p, page: 0 }));
  };

  const handleClearSearch = () => {
    setSearchValue('');
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const handlePageChange = (_, newPage) =>
    setPagination((p) => ({ ...p, page: newPage }));

  const handleRowsPerPageChange = (e) => {
    const sz = parseInt(e.target.value, 10);
    setPagination((p) => ({ ...p, page: 0, pageSize: sz }));
  };

  const setLeads = (updater) => {
    if (typeof updater === 'function') {
      dispatch(setLeadsList(updater));
    }
  };

  return {
    leads,
    tableLoading,
    searchValue,
    pagination,
    filters,
    sort,
    noActivity,
    statusOptions: statusFilterOptions,
    usersList: usersFilterOptions,
    processingId,
    statusFilterLoading,
    usersFilterLoading,
    setProcessingId,
    setLeads,
    fetchLeads,
    ensureStatusFilterOptions,
    ensureUsersFilterOptions,
    handleSearchChange,
    handleClearSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleFilterChange,
    applyStatusFilter,
    handleDateRangeChange,
    handleSortChange,
    handleNoActivityChange,
    clearFilters,
    getActiveFiltersCount,
  };
};
